import React from "react";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import TablePagination from "./TablePagination";
import "../../style/table.scss";
import "../../style/typography.scss";
import "../../style/colors.scss";

/**
 * Table component that wraps `@tanstack/react-table`.
 * @param {object} props
 * @param {import("../../hooks/useTable").CustomReactTable} props.table `@tanstack/react-table` object. Create this object using the custom `useTable` hook.
 * @param {(row: import("@tanstack/react-table").Row) => void} [props.onRowClick] Called when a row is clicked.
 * @param {React.ReactNode} [props.noDataComponent] Rendered when there's no data. Defaults to `"No data available"`.
 * @param {import("../../hooks/useTable").PaginationOptions} [props.paginationOptions] Options for pagination. Set to `null` or `undefined` to disable pagination (defaults to `undefined`).
 * @param {React.CSSProperties} [props.containerStyle] Style for the container div.
 */
function Table({
    table,
    onRowClick,
    noDataComponent = "No data available",
    containerStyle,
    minimumTableWidth = "750px",
}) {
    const rowModel = table.getRowModel();
    const headerGroups = table.getHeaderGroups();
    const hasData = rowModel.rows.length > 0;

    return (
        <div style={{ width: "100%", overflowX: "auto", maxHeight: "100%", ...containerStyle }}>
            <table className="table-primary" style={{ minWidth: minimumTableWidth }}>
                <TableHeader headerGroups={headerGroups} />

                {hasData && <TableBody rowModel={rowModel} onRowClick={onRowClick} />}
            </table>

            {!hasData && (
                <div
                    style={{
                        margin: "10px auto 0",
                        width: "fit-content",
                        color: "var(--colors-primary-slate-600)",
                    }}
                    className="paragraph-5-regular"
                >
                    {noDataComponent}
                </div>
            )}

            {table.paginationOptions && hasData && <TablePagination table={table} />}
        </div>
    );
}

export default Table;
